import { useEffect, useState } from 'react';
import { Product, ProductVariant } from '../types';
import { formatBRL } from '../utils/format';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import {
  ShoppingBag,
  ArrowLeft,
  Check,
  Truck,
  FileDown,
  Sparkles,
  Calendar,
  AlertCircle,
} from 'lucide-react';

interface ProductPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

export function ProductPage({ slug, onNavigate }: ProductPageProps) {
  const { addToCart } = useCart();
  const { settings } = useStore();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'desc' | 'specs' | 'delivery'>('desc');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/products/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error('Produto não encontrado');
        return res.json();
      })
      .then((data: any) => {
        setProduct(data);
        if (data.variants && data.variants.length > 0) {
          setSelectedVariant(data.variants[0]);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-[4/5] bg-surface-muted rounded-xs animate-pulse" />
        <div className="space-y-6">
          <div className="h-8 bg-surface-muted rounded-xs w-3/4 animate-pulse" />
          <div className="h-6 bg-surface-muted rounded-xs w-1/4 animate-pulse" />
          <div className="h-24 bg-surface-muted rounded-xs animate-pulse" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="text-2xl font-serif font-medium text-ink">Peça indisponível</h2>
        <p className="text-xs text-ink-muted">O produto selecionado não foi encontrado ou foi retirado do catálogo.</p>
        <button
          onClick={() => onNavigate('/catalogo')}
          className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Voltar ao Catálogo
        </button>
      </div>
    );
  }

  const currentPrice = selectedVariant ? selectedVariant.price_cents : product.base_price_cents;
  const isOutOfStock =
    selectedVariant &&
    selectedVariant.track_inventory === 1 &&
    selectedVariant.stock_quantity <= 0;

  const handleAddToCart = async () => {
    if (!selectedVariant || isOutOfStock) return;
    setAdding(true);
    await addToCart(selectedVariant.id, quantity);
    setAdding(false);
  };

  const images = product.images && product.images.length > 0
    ? product.images
    : [{ id: 'default', url: product.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=1000&q=85', alt_text: product.title, sort_order: 0 }];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
      {/* Breadcrumb & Navigation */}
      <button
        onClick={() => onNavigate('/catalogo')}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o Catálogo
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column: Image Gallery */}
        <div className="lg:col-span-7 space-y-4">
          <div className="relative aspect-[4/5] bg-background-alt border border-border-subtle rounded-xs overflow-hidden shadow-subtle">
            <img
              src={images[selectedImageIndex]?.url}
              alt={images[selectedImageIndex]?.alt_text || product.title}
              className="w-full h-full object-cover object-center transition-all duration-300"
            />
            {product.product_type === 'digital' && (
              <div className="absolute top-4 left-4 bg-surface/90 backdrop-blur-xs px-3 py-1.5 rounded-xs border border-border-subtle text-xs uppercase tracking-wider font-semibold text-primary flex items-center gap-1.5">
                <FileDown className="w-3.5 h-3.5" />
                Arquivo Digital Imediato
              </div>
            )}
            {product.product_type === 'service' && (
              <div className="absolute top-4 left-4 bg-surface/90 backdrop-blur-xs px-3 py-1.5 rounded-xs border border-border-subtle text-xs uppercase tracking-wider font-semibold text-primary flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Serviço Sob Demanda
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`relative w-20 h-24 rounded-xs overflow-hidden border transition-all shrink-0 ${
                    selectedImageIndex === idx ? 'border-primary ring-1 ring-primary' : 'border-border-subtle opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img.url} alt={img.alt_text} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Product Actions & Editorial Info */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            {product.categories && product.categories.length > 0 && (
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
                {product.categories[0].name}
              </span>
            )}
            <h1 className="text-3xl sm:text-4xl font-serif font-normal text-ink leading-tight">
              {product.title}
            </h1>
            <p className="text-xs text-ink-muted leading-relaxed">
              {product.summary}
            </p>
          </div>

          {/* Pricing */}
          <div className="py-3 border-y border-border-subtle flex items-baseline gap-3">
            <span className="font-mono text-2xl font-medium text-ink">
              {formatBRL(currentPrice)}
            </span>
            {product.compare_at_price_cents && product.compare_at_price_cents > currentPrice && (
              <span className="font-mono text-sm text-ink-muted line-through">
                {formatBRL(product.compare_at_price_cents)}
              </span>
            )}
            <span className="text-[11px] text-ink-muted">
              {product.product_type === 'physical' ? 'Em até 3x sem juros' : 'Pagamento único'}
            </span>
          </div>

          {/* Variant Selection */}
          {product.variants && product.variants.length > 1 && (
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider font-semibold text-ink block">
                Selecione o Acabamento / Variação
              </label>
              <div className="grid grid-cols-2 gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVariant(v)}
                    className={`p-3 text-left border rounded-xs transition-all text-xs ${
                      selectedVariant?.id === v.id
                        ? 'border-primary bg-primary-subtle text-ink font-medium ring-1 ring-primary'
                        : 'border-border-subtle bg-surface hover:border-ink/30 text-ink-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-ink">{v.title}</span>
                      {selectedVariant?.id === v.id && <Check className="w-3.5 h-3.5 text-primary" />}
                    </div>
                    <span className="font-mono text-[11px] text-ink-muted mt-1 block">
                      {formatBRL(v.price_cents)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stock Availability Indicator */}
          <div className="text-xs flex items-center gap-2">
            {product.product_type === 'physical' ? (
              selectedVariant?.track_inventory === 1 ? (
                selectedVariant.stock_quantity > 3 ? (
                  <span className="text-olive flex items-center gap-1.5 font-medium">
                    <span className="w-2 h-2 rounded-full bg-olive" />
                    Em estoque ({selectedVariant.stock_quantity} unidades prontas para envio)
                  </span>
                ) : selectedVariant.stock_quantity > 0 ? (
                  <span className="text-amber-700 flex items-center gap-1.5 font-medium">
                    <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
                    Últimas {selectedVariant.stock_quantity} unidades restantes
                  </span>
                ) : (
                  <span className="text-red-700 flex items-center gap-1.5 font-medium">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Esgotado no momento
                  </span>
                )
              ) : (
                <span className="text-olive flex items-center gap-1.5 font-medium">
                  <span className="w-2 h-2 rounded-full bg-olive" />
                  Produção contínua sob encomenda
                </span>
              )
            ) : product.product_type === 'digital' ? (
              <span className="text-olive flex items-center gap-1.5 font-medium">
                <Sparkles className="w-3.5 h-3.5" />
                Entrega digital imediata após confirmação
              </span>
            ) : (
              <span className="text-olive flex items-center gap-1.5 font-medium">
                <Calendar className="w-3.5 h-3.5" />
                Agendamento da sessão com a equipe de arquitetura
              </span>
            )}
          </div>

          {/* Quantity & Add to Cart Button */}
          <div className="space-y-3 pt-2">
            <div className="flex gap-3">
              {product.product_type === 'physical' && (
                <div className="flex items-center border border-border-subtle rounded-xs bg-surface">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={isOutOfStock || quantity <= 1}
                    className="px-3 py-3 text-ink-muted hover:text-ink disabled:opacity-30"
                    aria-label="Diminuir quantidade"
                  >
                    -
                  </button>
                  <span className="px-3 text-xs font-mono font-medium text-ink min-w-[32px] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() =>
                      setQuantity(
                        selectedVariant?.track_inventory === 1
                          ? Math.min(selectedVariant.stock_quantity, quantity + 1)
                          : quantity + 1
                      )
                    }
                    disabled={Boolean(isOutOfStock)}
                    className="px-3 py-3 text-ink-muted hover:text-ink disabled:opacity-30"
                    aria-label="Aumentar quantidade"
                  >
                    +
                  </button>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock || adding}
                className="flex-1 bg-ink text-ink-inverted py-3.5 px-6 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-subtle"
              >
                <ShoppingBag className="w-4 h-4" />
                {isOutOfStock ? 'Produto Esgotado' : adding ? 'Adicionando...' : 'Adicionar à Sacola'}
              </button>
            </div>

            {/* Free shipping reassurance */}
            <div className="bg-background-alt p-3 rounded-xs border border-border-subtle flex items-center gap-2.5 text-[11px] text-ink-muted">
              <Truck className="w-4 h-4 text-primary shrink-0" />
              <span>
                Frete cortesia para todo o Brasil em compras acima de {formatBRL(settings.shipping_free_threshold_cents || 45000)}.
              </span>
            </div>
          </div>

          {/* Tabs: Description, Specs, Delivery */}
          <div className="pt-6 border-t border-border-subtle space-y-4">
            <div className="flex border-b border-border-subtle gap-6 text-xs uppercase tracking-wider font-medium">
              <button
                onClick={() => setActiveTab('desc')}
                className={`pb-2 transition-colors ${
                  activeTab === 'desc'
                    ? 'border-b-2 border-primary text-ink font-semibold'
                    : 'text-ink-muted hover:text-ink'
                }`}
              >
                Curadoria & Detalhes
              </button>
              <button
                onClick={() => setActiveTab('specs')}
                className={`pb-2 transition-colors ${
                  activeTab === 'specs'
                    ? 'border-b-2 border-primary text-ink font-semibold'
                    : 'text-ink-muted hover:text-ink'
                }`}
              >
                Especificações
              </button>
              <button
                onClick={() => setActiveTab('delivery')}
                className={`pb-2 transition-colors ${
                  activeTab === 'delivery'
                    ? 'border-b-2 border-primary text-ink font-semibold'
                    : 'text-ink-muted hover:text-ink'
                }`}
              >
                {product.product_type === 'physical'
                  ? 'Envio & Cuidados'
                  : product.product_type === 'digital'
                  ? 'Licença de Uso'
                  : 'Instruções'}
              </button>
            </div>

            <div className="text-xs text-ink leading-relaxed">
              {activeTab === 'desc' && (
                <div
                  className="prose prose-sm max-w-none text-ink-muted space-y-2"
                  dangerouslySetInnerHTML={{ __html: product.description_html }}
                />
              )}

              {activeTab === 'specs' && (
                <div className="space-y-2">
                  {product.attributes && product.attributes.length > 0 ? (
                    <dl className="grid grid-cols-2 gap-2">
                      {product.attributes.map((attr) => (
                        <div key={attr.id} className="bg-surface p-2.5 rounded-xs border border-border-subtle">
                          <dt className="text-ink-muted font-medium text-[11px]">{attr.name}</dt>
                          <dd className="text-ink font-medium mt-0.5">{attr.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="text-ink-muted">Nenhuma especificação técnica adicional registrada.</p>
                  )}
                  {selectedVariant && (
                    <p className="text-[11px] text-ink-muted font-mono pt-2">
                      SKU da Peça: {selectedVariant.sku}
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'delivery' && (
                <div className="space-y-2 text-ink-muted">
                  {product.product_type === 'physical' ? (
                    <>
                      <p>
                        <strong>Embalagem segura:</strong> Nossas cerâmicas são acondicionadas em caixas reforçadas com mantas biodegradáveis para garantir chegada intacta.
                      </p>
                      <p>
                        <strong>Prazo de postagem:</strong> Peças em estoque são postadas em até 2 dias úteis via transportadora parceira rastreada.
                      </p>
                      <p>
                        <strong>Troca garantida:</strong> Se houver qualquer avaria no transporte, realizamos a substituição imediata sem custo adicional.
                      </p>
                    </>
                  ) : product.product_type === 'digital' ? (
                    <>
                      <p>
                        <strong>Acesso aos arquivos:</strong> O download do arquivo .ZIP ou .PDF fica disponível na sua página de pedidos imediatamente após a confirmação do pagamento.
                      </p>
                      <p>
                        <strong>Licença:</strong> Uso individual comercial ou pessoal permitido. É vedada a revenda ou redistribuição dos arquivos originais.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Próximos passos:</strong> Após a compra, nossa arquiteta enviará em até 24h úteis o link para agendamento do encontro virtual e envio da planta baixa do cômodo.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
